import type { ColumnDef } from '@tanstack/react-table';
import { DataTable } from '@/components/data-table/data-table';
import { QueryErrorBanner } from '@/components/feedback/query-error';
import { useFrickBalances } from '@/hooks/queries/treasury/use-frick-balances';
import type { FrickAccountBalance } from '@/services/api/frick';
import { formatCurrency } from '@/utils/format';

const ERROR_MESSAGE = 'Failed to fetch. Try reloading a few times to build our cache. If still failing, try again in a few minutes';

const COLUMNS: ColumnDef<FrickAccountBalance, unknown>[] = [
  {
    accessorKey: 'iban',
    header: 'Iban',
    cell: ({ row }) => <span className="whitespace-nowrap font-mono text-xs">{row.original.iban}</span>,
  },
  {
    accessorKey: 'currency',
    header: 'Currency',
  },
  {
    accessorKey: 'balance',
    header: 'Current balance',
    cell: ({ row }) => <span className="whitespace-nowrap tabular-nums">{formatCurrency(row.original.balance, row.original.currency)}</span>,
  },
  {
    accessorKey: 'available',
    header: 'Available balance',
    cell: ({ row }) => <span className="whitespace-nowrap tabular-nums">{formatCurrency(row.original.available, row.original.currency)}</span>,
  },
  {
    id: 'other',
    header: 'Other data',
    enableSorting: false,
    cell: ({ row }) => (
      <span className="flex flex-col gap-0.5 text-xs text-muted-foreground">
        <span>{row.original.refCurrency}</span>
        <span>{row.original.account}</span>
        <span>{row.original.customer}</span>
        <span>{row.original.type}</span>
        <span>{row.original.category}</span>
      </span>
    ),
  },
];

const FrickBalancesPage = () => {
  const { data, isPending, isError, error, refetch } = useFrickBalances();

  return (
    <div className="flex flex-col gap-4">
      <h1 className="text-3xl font-semibold">Frick Balances</h1>

      {isError ? (
        <QueryErrorBanner error={error} onRetry={() => refetch()} title={ERROR_MESSAGE} />
      ) : (
        // Row key iban+currency — the legacy page keyed on iban alone, colliding
        // for multi-currency accounts (deliberate fix).
        <DataTable columns={COLUMNS} data={data} emptyMessage="No balances" getRowId={(row) => `${row.iban}-${row.currency}`} isLoading={isPending} />
      )}
    </div>
  );
};
FrickBalancesPage.displayName = 'FrickBalancesPage';

export default FrickBalancesPage;
